// src/components/laboratory/modals/ConfirmDeleteModal.jsx (Generic Version)
import React, { useState } from 'react';
import { useMutation } from '@apollo/client';
import { 
  DELETE_LABORATORY,
  DELETE_PRODUCT,
  REMOVE_PROMOTION,
  DELETE_CONTRACT,
  DELETE_ADD,
  REMOVE_STAFF_MEMBER
} from '../../../api/laboratory-api';
import ModalComponent from '../../common/ModalComponent';
import { AlertCircle } from 'lucide-react';
import { toast } from 'react-toastify';

const ConfirmDeleteModal = ({ 
  type = 'laboratory', 
  item, 
  laboratory, 
  user,
  isOpen, 
  onClose, 
  onConfirm 
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Determine which mutation to use based on type
  const getMutation = () => {
    switch (type) {
      case 'laboratory':
        return DELETE_LABORATORY;
      case 'product':
        return DELETE_PRODUCT;
      case 'promotion':
        return REMOVE_PROMOTION;
      case 'contract':
        return DELETE_CONTRACT;
      case 'ad':
        return DELETE_ADD;
      case 'user':
        return REMOVE_STAFF_MEMBER;
      default:
        return DELETE_LABORATORY;
    }
  };

  const [performDelete] = useMutation(getMutation(), {
    onCompleted: () => {
      const successMessages = {
        laboratory: 'Laboratoire supprimé avec succès',
        product: 'Produit supprimé avec succès',
        promotion: 'Promotion supprimée avec succès',
        contract: 'Contrat supprimé avec succès',
        ad: 'Publicité supprimée avec succès',
        user: 'Utilisateur retiré avec succès'
      };
      toast.success(successMessages[type]);
      onConfirm();
    },
    onError: (error) => {
      const actionWord = type === 'user' ? 'retrait' : 'suppression';
      toast.error(`Erreur lors de la ${actionWord}: ${error.message}`);
      setIsSubmitting(false);
    }
  });

  const handleConfirmAction = async () => {
    setIsSubmitting(true);
    try {
      const variables = type === 'user' 
        ? { laboratoryId: laboratory.id, userId: (user || item).id }
        : { id: (item || laboratory).id };
      
      await performDelete({ variables });
    } catch (error) {
      console.error(`Error ${type === 'user' ? 'removing' : 'deleting'} ${type}:`, error);
      setIsSubmitting(false);
    }
  };

  // Get content based on type
  const getContent = () => {
    switch (type) {
      case 'laboratory':
        return {
          title: 'Confirmer la suppression',
          subject: laboratory,
          action: 'supprimer',
          name: laboratory?.nom,
          warning: 'Cette action est irréversible et supprimera également tous les éléments associés à ce laboratoire.',
          relations: getLabRelations(),
          canPerform: true
        };
      
      case 'product':
        return {
          title: 'Confirmer la suppression',
          subject: item,
          action: 'supprimer',
          name: item?.name,
          warning: 'Cette action est irréversible et supprimera également toutes les promotions et labels associés à ce produit.',
          relations: [],
          canPerform: true
        };
      
      case 'promotion':
        const isInProgress = item?.status === 'IN_PROGRESS';
        return {
          title: 'Confirmer la suppression',
          subject: item,
          action: 'supprimer',
          name: item?.name,
          warning: isInProgress 
            ? 'Cette promotion est actuellement active. Sa suppression arrêtera immédiatement la promotion et supprimera tous les codes QR associés.'
            : 'Cette action est irréversible et supprimera également tous les codes QR et labels associés à cette promotion.',
          relations: [],
          canPerform: true // CHANGED: Always allow deletion
        };
      
      case 'user':
        const userItem = user || item;
        const isOwner = laboratory?.userId === userItem?.id;
        return {
          title: 'Confirmer le retrait',
          subject: userItem,
          action: 'retirer',
          name: `${userItem?.firstName} ${userItem?.lastName} (${userItem?.email})`,
          warning: isOwner 
            ? 'Impossible de retirer le propriétaire du laboratoire.'
            : 'L\'utilisateur n\'aura plus accès aux données et fonctionnalités de ce laboratoire.',
          relations: [],
          canPerform: !isOwner
        };
      
      default:
        return {
          title: 'Confirmer la suppression',
          subject: item,
          action: 'supprimer',
          name: item?.name || item?.nom || 'cet élément',
          warning: 'Cette action est irréversible.',
          relations: [],
          canPerform: true
        };
    }
  };

  const getLabRelations = () => {
    if (type !== 'laboratory' || !laboratory) return [];
    
    const hasProducts = laboratory?.products?.length > 0;
    const hasPromotions = laboratory?.promotions?.length > 0;
    const hasUsers = laboratory?.users?.length > 0;
    const hasContracts = laboratory?.contrats?.length > 0;

    return [
      hasProducts && `${laboratory.products.length} produit${laboratory.products.length > 1 ? 's' : ''}`,
      hasPromotions && `${laboratory.promotions.length} promotion${laboratory.promotions.length > 1 ? 's' : ''}`,
      hasUsers && `${laboratory.users.length} utilisateur${laboratory.users.length > 1 ? 's' : ''}`,
      hasContracts && `${laboratory.contrats.length} contrat${laboratory.contrats.length > 1 ? 's' : ''}`
    ].filter(Boolean);
  };

  const content = getContent();

  return (
    <ModalComponent
      isOpen={isOpen}
      onClose={onClose}
      title={content.title}
      onSubmit={content.canPerform ? handleConfirmAction : null}
      isSubmitting={isSubmitting}
      submitLabel={content.canPerform ? content.action.charAt(0).toUpperCase() + content.action.slice(1) : "Impossible"}
      submitButtonClassName={content.canPerform ? "bg-red-600 hover:bg-red-700 focus:ring-red-500" : "bg-gray-400 cursor-not-allowed"}
    >
      <div className="space-y-4">
        <div className="flex items-center space-x-4 text-red-600">
          <AlertCircle size={24} />
          <h3 className="text-lg font-medium">Attention</h3>
        </div>
        
        <p className="text-gray-600">
          Êtes-vous sûr de vouloir {content.action} {type === 'laboratory' ? 'le laboratoire' : type === 'user' ? '' : `${type === 'promotion' ? 'la promotion' : type === 'product' ? 'le produit' : 'le'}`} <strong>{content.name}</strong> {type === 'user' ? `du laboratoire ${laboratory?.nom}` : ''} ?
        </p>
        
        {!content.canPerform ? (
          <div className="mt-4 p-4 bg-yellow-50 border-l-4 border-yellow-400 text-yellow-700 rounded-md">
            <p className="font-medium">
              ⚠️ {content.warning}
            </p>
            {type === 'promotion' && (
              <p className="mt-2 text-sm">
                Veuillez d'abord changer le statut de la promotion ou attendre sa fin.
              </p>
            )}
            {type === 'user' && (
              <p className="mt-2 text-sm">
                Pour changer le propriétaire, utilisez la fonction "Modifier le laboratoire".
              </p>
            )}
          </div>
        ) : (
          <>
            <p className="text-gray-600">
              {content.warning}
            </p>
            
            {content.relations.length > 0 && (
              <div className="mt-4 p-4 bg-red-50 border-l-4 border-red-400 text-red-700 rounded-md">
                <p className="font-medium mb-2">
                  Ce laboratoire contient :
                </p>
                <ul className="list-disc list-inside space-y-1">
                  {content.relations.map((relation, index) => (
                    <li key={index}>{relation}</li>
                  ))}
                </ul>
                <p className="mt-2 text-sm">
                  Tous ces éléments seront définitivement supprimés avec le laboratoire.
                </p>
              </div>
            )}
            
            {type === 'user' && (
              <div className="mt-4 p-4 bg-blue-50 border-l-4 border-blue-400 text-blue-700 rounded-md">
                <p className="font-medium">
                  ℹ️ Note : L'utilisateur ne sera pas supprimé du système, seulement retiré de l'équipe de ce laboratoire.
                </p>
              </div>
            )}
          </>
        )}
      </div>
    </ModalComponent>
  );
};

export default ConfirmDeleteModal;